import {isFilled} from '@prismicio/client'
import {GetStaticProps} from 'next'
import {createClient} from 'prismicio'
import {
  ArticleDocument,
  ButtonDocument,
  DialogSlice,
  FormSectionSlice,
  GenericFormDocument,
  NavigationDocumentData,
  PageDocument,
  PageDocumentDataSlicesSlice,
  ProductAnnouncementCardDocument,
  ProductDeepDiveDocument,
  ProductDeepDiveDocumentData,
  ProductDeepDiveSlice,
  ProductDeepDiveVerticalScrollDocument,
} from 'prismicio-types'
import {Page} from 'src/Page'
import {NAVIGATION_QUERY} from 'src/common/constants'
import {wrongUids} from 'src/common/helpers'

export default Page

export const getStaticPaths = async () => ({
  paths: (await createClient().getAllByType('page', {lang: '*'}))
    .filter((page) => !isFilled.contentRelationship(page.data.parent_page))
    .filter((page) => page.uid !== 'finance-guide') // Exclude finance-guide as it has its own route
    .map((page) => ({params: {uid: page.uid}, locale: page.lang})),
  fallback: false,
})

export const getStaticProps: GetStaticProps<
  | {
      articles: Array<ArticleDocument>
      navigationData: NavigationDocumentData
      page: PageDocument
      redirect: never
      productDeepDiveData: ProductDeepDiveDocumentData | null
      formData?: GenericFormDocument
      productAnnouncementsData?: Array<ProductAnnouncementCardDocument>
      buttonsData?: ButtonDocument[]
    }
  | {},
  {uid: string}
> = async ({params, locale, previewData}) => {
  if (params?.uid && wrongUids.some((uid) => params?.uid.includes(uid))) {
    return {notFound: true}
  }

  if (params?.uid === 'guide') {
    return {
      redirect: {
        destination:
          locale === 'en-gb' || locale === 'en-us'
            ? 'https://www.getmoss.com/guide/en'
            : 'https://www.getmoss.com/guide',
        permanent: true,
      },

      props: {},
    }
  }

  const client = createClient({previewData})
  let articles: Array<ArticleDocument> = []
  let navigationData
  let page
  let productDeepDiveData
  let formData
  let dialogData
  let productAnnouncementsData
  let buttonsData: ButtonDocument[] = []

  try {
    navigationData = (
      await client.getSingle('navigation', {
        fetchLinks: ['page.parent_page'],
        lang: locale,
        graphQuery: NAVIGATION_QUERY,
      })
    ).data

    page = params
      ? await client.getByUID('page', params.uid, {
          fetchLinks: [
            'article.tag',
            'article.title',
            'article.author',
            'article.poster',
            'author.photo',
            'author.name',
            'page.parent_page',
            'partner_category.name',
            'blog_post.title',
            'blog_post.description',
            'blog_post.meta_image',
            'blog_post.category',
            'blog_post.date',
            'blog_post.author',
            'blog_post.parent_page',
          ],
          lang: locale,
        })
      : null

    buttonsData = (await client.getAllByType<ButtonDocument>('button', {lang: locale})) as ButtonDocument[]

    if (page && page.data.slices.find((slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'magazine')) {
      articles = await client.getAllByType('article', {
        graphQuery: `
          {
            article {
              author {
                ...on author {
                  photo
                  name
                }
              }

              is_featured
              tag
              poster
              title
              parent_page
            }
          }
        `,
        lang: locale,
        orderings: {field: 'my.article.date', direction: 'desc'},
      })
    }
  } catch (error) {
    navigationData = null
    page = null
    process.stderr.write(JSON.stringify(error))
  }

  if (!page || isFilled.contentRelationship(page?.data.parent_page) || !navigationData) {
    throw new Error('Unable to get data from Prismic')
  }

  const form = page.data.slices.find(
    (slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'form_section',
  ) as unknown as FormSectionSlice

  if (form && isFilled.contentRelationship(form.primary.form_document)) {
    formData = await client.getByUID('generic_form', form.primary.form_document.uid || '', {lang: locale})
  }

  const dialog = page.data.slices.find(
    (slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'dialog',
  ) as unknown as DialogSlice

  if (dialog && isFilled.contentRelationship(dialog.primary.dialog_content_id)) {
    dialogData = await client.getByID(dialog.primary.dialog_content_id.id, {lang: locale})
  }

  const productAnnouncementsContainer = page.data.slices.find(
    (slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'product_announcements_container',
  )

  if (productAnnouncementsContainer) {
    const data = await client.getAllByType('product_announcement_card', {lang: locale})

    productAnnouncementsData = data.sort(
      ({data: {date: aDate}}, {data: {date: bDate}}) =>
        Number(new Date(bDate as string)) - Number(new Date(aDate as string)),
    )
  }

  const productDeepDiveSlice = page.data.slices.find(
    (slice: PageDocumentDataSlicesSlice) => slice.slice_type === 'product_deep_dive',
  ) as ProductDeepDiveSlice

  if (productDeepDiveSlice) {
    let requestedType: ProductDeepDiveDocument['type'] | ProductDeepDiveVerticalScrollDocument['type']
    let documentUid

    switch (productDeepDiveSlice.variation) {
      case 'verticalScroll':
        requestedType = 'product_deep_dive_vertical_scroll'

        documentUid = isFilled.contentRelationship(productDeepDiveSlice.primary.sections)
          ? productDeepDiveSlice.primary.sections.uid!
          : null

        break

      case 'singleSection':
        requestedType = 'product_deep_dive'
        documentUid = null

        break

      default:
        requestedType = 'product_deep_dive'

        documentUid = isFilled.contentRelationship(productDeepDiveSlice.primary.sections)
          ? productDeepDiveSlice.primary.sections.uid!
          : null
    }

    if (documentUid) {
      productDeepDiveData = (await client.getByUID(requestedType, documentUid, {lang: locale})).data
    }
  }

  return {
    props: {
      articles,
      navigationData,
      page,
      ...(productDeepDiveData && {productDeepDiveData}),
      ...(formData ? {formData} : {}),
      ...(dialogData ? {dialogData} : {}),
      ...(productAnnouncementsData ? {productAnnouncementsData} : {}),
      ...(buttonsData.length > 0 ? {buttonsData} : {}),
    },

    revalidate: Number(process.env.REVALIDATE_TIME) || 300,
  }
}
